import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { api, Group, Player } from "../api/client";
import { feesApi, formatRubles } from "../api/fees";
import {
  canCancel,
  formatClubDate,
  formatPeriod,
  Invoice,
  InvoiceFilters,
  INVOICE_STATUSES,
  invoicePeriods,
  invoicesApi,
  statusLabel,
} from "../api/invoices";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./InvoicesPage.css";

const PAGE_SIZE = 50;

const NO_FILTERS: InvoiceFilters = { status: "", groupID: "", month: "" };

type FormState = {
  playerID: string;
  month: string;
  amount: string;
  amountTouched: boolean;
  description: string;
  dueDate: string;
};

type Prices = { byGroup: Map<string, number>; base: number | null };

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const emptyForm = (): FormState => ({
  playerID: "",
  month: currentMonth(),
  amount: "",
  amountTouched: false,
  description: "",
  dueDate: "",
});

function badgeClass(status: string): string {
  if (status === "completed" || status === "manual") return "mav-badge-active";
  if (status === "pending") return "mav-badge-dirty";
  if (status === "failed" || status === "expired") return "invoice-badge-danger";
  return "mav-badge-muted";
}

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState<InvoiceFilters>(NO_FILTERS);
  const [players, setPlayers] = useState<Player[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [prices, setPrices] = useState<Prices | null>(null);
  const [refsLoaded, setRefsLoaded] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const listRequest = useRef(0);
  const { confirm, dialog } = useConfirm();

  const loadList = useCallback(async (f: InvoiceFilters, off: number) => {
    // Filters can change faster than the server answers; only the latest answer counts.
    const requestID = ++listRequest.current;
    try {
      const page = (await invoicesApi.list(f, off, PAGE_SIZE + 1)) ?? [];
      if (requestID !== listRequest.current) return;
      setInvoices(page.slice(0, PAGE_SIZE));
      setHasMore(page.length > PAGE_SIZE);
    } catch (err: any) {
      if (requestID !== listRequest.current) return;
      console.error("Failed to load invoices", err);
      setError(err?.message || "Не удалось загрузить счета");
      setInvoices([]);
      setHasMore(false);
    } finally {
      setListLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadList(filters, offset);
  }, [filters, offset, loadList]);

  useEffect(() => {
    (async () => {
      try {
        const [playerList, groupList] = await Promise.all([api.listPlayers(), api.listGroups()]);
        setPlayers((playerList ?? []).filter((p) => p.is_active).sort(byName));
        setGroups((groupList ?? []).slice().sort(byName));
      } catch (err: any) {
        console.error("Failed to load players and groups", err);
        setError(err?.message || "Не удалось загрузить детей и группы");
      }
      // Prices only prefill the amount; the admin can still type it in.
      try {
        const [groupFees, base] = await Promise.all([feesApi.listGroupFees(), feesApi.getBaseFee()]);
        const byGroup = new Map<string, number>();
        for (const g of groupFees ?? []) {
          if (g.has_override && g.monthly_fee) byGroup.set(g.group_id, g.monthly_fee);
        }
        setPrices({ byGroup, base: base?.monthly_fee ?? null });
      } catch (err) {
        console.error("Failed to load prices", err);
      }
      setRefsLoaded(true);
    })();
  }, []);

  const groupName = new Map(groups.map((g) => [g.id, g.name]));
  const selectedPlayer = players.find((p) => p.id === form.playerID) ?? null;
  const amount = Number(form.amount);
  const canSubmit = !busy && selectedPlayer !== null && amount > 0 && /^\d{4}-\d{2}$/.test(form.month);

  const set = (changes: Partial<FormState>) => setForm((f) => ({ ...f, ...changes }));

  const pickPlayer = (playerID: string) => {
    setForm((f) => {
      if (f.amountTouched) return { ...f, playerID };
      const player = players.find((p) => p.id === playerID);
      const price =
        (player?.group_id ? prices?.byGroup.get(player.group_id) : undefined) ?? prices?.base ?? null;
      return { ...f, playerID, amount: price ? String(price) : "" };
    });
  };

  const changeFilter = (changes: Partial<InvoiceFilters>) => {
    setError(null);
    setFilters((f) => ({ ...f, ...changes }));
    setOffset(0);
  };

  const reloadFirstPage = async () => {
    if (offset === 0) await loadList(filters, 0);
    else setOffset(0);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !selectedPlayer) return;
    const description = form.description.trim();
    const [year, month] = form.month.split("-").map(Number);
    const confirmed = await confirm({
      title: "Выставить счёт?",
      body: (
        <>
          <p>
            {selectedPlayer.name}: {description || "тренировки"} за {formatPeriod(month, year)} —{" "}
            {formatRubles(amount)}.
          </p>
          <p>Родители ребёнка сразу получат уведомление и смогут оплатить счёт в приложении.</p>
        </>
      ),
      confirmLabel: "Выставить счёт",
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await invoicesApi.issue({
        playerID: selectedPlayer.id,
        month: form.month,
        amount,
        description,
        dueDate: form.dueDate,
      });
      setForm(emptyForm());
      await reloadFirstPage();
    } catch (err: any) {
      console.error("Invoice issue failed", err);
      setError(err?.message || "Не удалось выставить счёт");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (invoice: Invoice) => {
    const confirmed = await confirm({
      title: invoice.description ? `Отозвать счёт «${invoice.description}»?` : "Отозвать счёт?",
      body: (
        <>
          <p>
            {invoice.player_names || "Счёт"} — {formatRubles(invoice.amount)}. После отзыва оплатить его будет
            нельзя: у родителей счёт уйдёт в историю со статусом «Отозвано», и им придёт уведомление, что платить
            не нужно.
          </p>
          <p>Вернуть отозванный счёт нельзя — если понадобится, выставьте новый.</p>
        </>
      ),
      confirmLabel: "Отозвать счёт",
      danger: true,
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await invoicesApi.cancel(invoice.id);
    } catch (err: any) {
      console.error("Invoice cancel failed", err);
      setError(err?.message || "Не удалось отозвать счёт");
    } finally {
      setBusy(false);
      await loadList(filters, offset);
    }
  };

  if (!refsLoaded || !listLoaded) return <PageLoader />;

  const filtered = filters.status !== "" || filters.groupID !== "" || filters.month !== "";

  return (
    <div className="page page-wide">
      <h2>Счета</h2>
      <p className="page-description">
        Счета родителям за тренировки и разовые покупки. Родители оплачивают их в приложении — отметить оплату или
        вернуть деньги здесь нельзя. Неоплаченный счёт можно отозвать.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <form className="mav-card cp-form invoice-form" aria-label="Новый счёт" onSubmit={submit}>
        <h3 className="invoice-form-title">Новый счёт</h3>
        <label className="cp-field">
          <span>Ребёнок</span>
          <select
            className="mav-input"
            value={form.playerID}
            disabled={busy}
            onChange={(e) => pickPlayer(e.target.value)}
          >
            <option value="">Выберите ребёнка</option>
            {players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.group_id && groupName.has(p.group_id) ? `${p.name} — ${groupName.get(p.group_id)}` : p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="cp-field">
          <span>За месяц</span>
          <input
            className="mav-input"
            type="month"
            value={form.month}
            disabled={busy}
            onChange={(e) => set({ month: e.target.value })}
          />
        </label>
        <div className="cp-field">
          <label className="cp-field">
            <span>Сумма, ₽</span>
            <input
              className="mav-input"
              type="number"
              min="1"
              step="0.01"
              inputMode="decimal"
              value={form.amount}
              disabled={busy}
              onChange={(e) => set({ amount: e.target.value, amountTouched: true })}
            />
          </label>
          <span className="mav-hint">Подставляется цена группы ребёнка, а если её нет — базовая</span>
        </div>
        <div className="cp-field">
          <label className="cp-field">
            <span>Назначение</span>
            <input
              className="mav-input"
              value={form.description}
              maxLength={100}
              placeholder="Например, форма"
              disabled={busy}
              onChange={(e) => set({ description: e.target.value })}
            />
          </label>
          <span className="mav-hint">Если пусто — «Тренировки ММ/ГГГГ»</span>
        </div>
        <div className="cp-field">
          <label className="cp-field">
            <span>Оплатить до</span>
            <input
              className="mav-input"
              type="date"
              value={form.dueDate}
              disabled={busy}
              onChange={(e) => set({ dueDate: e.target.value })}
            />
          </label>
          <span className="mav-hint">Если пусто — до конца выбранного месяца</span>
        </div>
        <div className="cp-actions mav-actions">
          <button type="submit" className="mav-btn-primary" disabled={!canSubmit}>
            Выставить счёт
          </button>
        </div>
      </form>

      <div className="invoices-filters" role="group" aria-label="Фильтры">
        <label className="cp-field">
          <span>Статус</span>
          <select className="mav-input" value={filters.status} onChange={(e) => changeFilter({ status: e.target.value })}>
            <option value="">Все статусы</option>
            {INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="cp-field">
          <span>Группа</span>
          <select className="mav-input" value={filters.groupID} onChange={(e) => changeFilter({ groupID: e.target.value })}>
            <option value="">Все группы</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label className="cp-field">
          <span>Месяц</span>
          <input
            className="mav-input"
            type="month"
            value={filters.month}
            onChange={(e) => changeFilter({ month: e.target.value })}
          />
        </label>
      </div>

      <div className="invoices-pager">
        <span>{invoices.length > 0 ? `Счета ${offset + 1}–${offset + invoices.length}` : ""}</span>
        {(offset > 0 || hasMore) && (
          <span className="invoices-pager-buttons">
            <button
              type="button"
              className="mav-btn-secondary"
              disabled={busy || offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              Назад
            </button>
            <button
              type="button"
              className="mav-btn-secondary"
              disabled={busy || !hasMore}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Дальше
            </button>
          </span>
        )}
      </div>

      <div className="table-wrap">
        <table className="settings-table stack-table invoices-table" aria-label="Счета">
          <thead>
            <tr>
              <th>Ребёнок</th>
              <th>Счёт</th>
              <th>Сумма</th>
              <th>Даты</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-cell">
                  {error ? "—" : filtered ? "По этим фильтрам счетов нет" : "Счетов пока нет"}
                </td>
              </tr>
            ) : (
              invoices.map((inv) => (
                <tr key={inv.id}>
                  <td className="cell-invoice-player">
                    <span className="invoice-line">{inv.player_names || "—"}</span>
                    {inv.group_names && <span className="invoice-sub">{inv.group_names}</span>}
                    {inv.parent_names && (
                      <span className="invoice-sub">
                        Родители: <span>{inv.parent_names}</span>
                      </span>
                    )}
                  </td>
                  <td className="cell-invoice-what">
                    <span className="invoice-line">{inv.description || "Счёт"}</span>
                    {invoicePeriods(inv.items).map((period) => (
                      <span key={period} className="invoice-sub">
                        {period}
                      </span>
                    ))}
                  </td>
                  <td className="cell-invoice-amount">
                    <span className="invoice-amount">{formatRubles(inv.amount)}</span>
                    <span className={`mav-badge ${badgeClass(inv.status)}`}>{statusLabel(inv.status)}</span>
                  </td>
                  <td className="date-cell cell-invoice-dates">
                    <span>выставлен {formatClubDate(inv.created_at)}</span>
                    {inv.expires_at && <span>оплатить до {formatClubDate(inv.expires_at)}</span>}
                    {inv.paid_at && <span>оплачен {formatClubDate(inv.paid_at)}</span>}
                  </td>
                  <td className="cell-action">
                    {canCancel(inv.status) && (
                      <div className="row-actions">
                        <button className="btn-quiet-danger" disabled={busy} onClick={() => cancel(inv)}>
                          Отозвать
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {dialog}
    </div>
  );
}
